/**
 * Math Validators — Numeric computation and arithmetic question types.
 *
 * Extracted from validatorRegistry.ts during R1-S1 god-file split.
 * Covers: multiple_choice, cijferend_rekenen, cijferend_delen, clock, money,
 *         fill_in, number_sequence, ratio_table, estimation_slider,
 *         grid_shade, step_solver, multiplication_grid, thermometer_slider.
 */

import type { Question, MoneyDenom } from '../../types'
import { error as logError } from '../../../utils/logger'
import { formatMoneyDisplay } from '../../../utils/moneyFormatters'
import { type ValidatorFn, NUMERIC_TOLERANCE, normalizeNumeric } from './shared'

// ── Multiple Choice ──────────────────────────────────────────

export const validateMultipleChoice: ValidatorFn = (userAnswer, question) => ({
  isCorrect: Number(userAnswer) === question.correctAnswer,
  distractorIndex: Number(userAnswer),
})

// ── Column Arithmetic (cijferend_rekenen) ────────────────────

export const validateColumnArithmetic: ValidatorFn = (userAnswer, question) => {
  const normalizedUser = String(userAnswer).replace(/\./g, '')

  let correctAnswer: string
  if (
    question.data?.operand1 !== undefined &&
    question.data?.operand2 !== undefined
  ) {
    const op1 = Number(question.data.operand1)
    const op2 = Number(question.data.operand2)
    const op = question.data?.operation
    const isSubtraction = op === '-'
    const isMultiplication = op === '×' || op === '*'
    const result = isMultiplication
      ? op1 * op2
      : isSubtraction
        ? op1 - op2
        : op1 + op2
    correctAnswer = String(result)
  } else if (
    question.options &&
    question.options.length > 0 &&
    typeof question.correctAnswer === 'number'
  ) {
    correctAnswer = question.options[question.correctAnswer].replace(/\./g, '')
  } else {
    correctAnswer = String(question.correctAnswer).replace(/\./g, '')
  }

  const isCorrect = normalizedUser === correctAnswer
  let distractorIndex = 0
  if (question.options && question.options.length > 0) {
    distractorIndex = question.options.findIndex(
      (opt) => opt.replace(/\./g, '') === normalizedUser,
    )
    if (distractorIndex === -1) distractorIndex = 0
  }
  return {
    isCorrect,
    distractorIndex,
    displayAnswer: String(userAnswer),
  }
}

// ── Long Division (cijferend_delen) ──────────────────────────

export const validateLongDivision: ValidatorFn = (userAnswer, question) => {
  const normalizedUser = String(userAnswer).replace(/\./g, '')

  let correctAnswer: string
  if (
    question.data?.operand1 !== undefined &&
    question.data?.operand2 !== undefined
  ) {
    const op1 = Number(question.data.operand1)
    const op2 = Number(question.data.operand2)
    correctAnswer = String(Math.floor(op1 / op2))
  } else if (
    question.options &&
    question.options.length > 0 &&
    typeof question.correctAnswer === 'number'
  ) {
    correctAnswer = question.options[question.correctAnswer].replace(/\./g, '')
  } else {
    correctAnswer = String(question.correctAnswer).replace(/\./g, '')
  }

  const isCorrect = normalizedUser === correctAnswer
  let distractorIndex = 0
  if (question.options && question.options.length > 0) {
    distractorIndex = question.options.findIndex(
      (opt) => opt.replace(/\./g, '') === normalizedUser,
    )
    if (distractorIndex === -1) distractorIndex = 0
  }

  return {
    isCorrect,
    distractorIndex,
    displayAnswer: String(userAnswer),
  }
}

// ── Clock ────────────────────────────────────────────────────

export const validateClock: ValidatorFn = (userAnswer, question) => {
  const userStr = String(userAnswer).trim()
  const [userHoursStr, userMinutesStr] = userStr.split(':')
  const userHours = parseInt(userHoursStr, 10) || 0
  const userMinutes = parseInt(userMinutesStr, 10) || 0

  if (!question.data?.clock) {
    logError('validateClock: Missing clock config in question.data.clock')
    return {
      isCorrect: false,
      distractorIndex: 0,
      displayAnswer: userStr,
      errorType: 'MISSING_CONFIG',
    }
  }
  const clockConfig = question.data.clock
  const correctHours = clockConfig?.target?.hours || 0
  const correctMinutes = clockConfig?.target?.minutes || 0

  // For set_analog mode: an analog clock has no AM/PM distinction,
  // so 03:15 and 15:15 are visually identical → both correct.
  const isSetAnalogMode = clockConfig?.mode === 'set_analog'
  const isCorrect = isSetAnalogMode
    ? userHours % 12 === correctHours % 12 && userMinutes === correctMinutes
    : userHours === correctHours && userMinutes === correctMinutes

  const userTotalMinutes = userHours * 60 + userMinutes
  const correctTotalMinutes = correctHours * 60 + correctMinutes

  const directDelta = Math.abs(userTotalMinutes - correctTotalMinutes)
  const wrapDelta = 1440 - directDelta
  const delta = Math.min(directDelta, wrapDelta)

  let errorType: string | undefined
  if (!isCorrect) {
    if (userHours !== correctHours && userMinutes === correctMinutes) {
      errorType = 'HOUR_OFF'
    } else if (userHours === correctHours && userMinutes !== correctMinutes) {
      if (delta === 5) errorType = 'MINUTE_OFF_5'
      else if (delta === 10) errorType = 'MINUTE_OFF_10'
      else if (delta === 15) errorType = 'MINUTE_OFF_15'
      else if (delta === 30) errorType = 'HALF_CONFUSION'
      else errorType = 'MINUTE_OFF'
    } else if (
      Math.abs(userHours - correctHours) === 12 &&
      userMinutes === correctMinutes
    ) {
      errorType = 'FORMAT_24H'
    } else {
      errorType = 'BOTH_OFF'
    }
  }

  let distractorIndex =
    question.options?.findIndex((opt) => opt === userStr) ?? -1
  if (distractorIndex === -1) distractorIndex = 0
  return {
    isCorrect,
    distractorIndex,
    displayAnswer: userStr,
    correctAnswerDisplay: `${correctHours.toString().padStart(2, '0')}:${correctMinutes.toString().padStart(2, '0')}`,
    errorType,
    delta,
  }
}

// ── Money ────────────────────────────────────────────────────

export const validateMoney: ValidatorFn = (userAnswer, question) => {
  let userTokens: Array<{ valueCents: number; count: number }> = []
  let userTotalCents = 0
  try {
    userTokens = JSON.parse(String(userAnswer))
    if (!Array.isArray(userTokens)) {
      throw new Error('Expected an array of money tokens')
    }
    if (
      !userTokens.every(
        (token) =>
          Number.isFinite(token?.valueCents) && Number.isFinite(token?.count),
      )
    ) {
      throw new Error(
        'Each money token must include finite valueCents and count numbers',
      )
    }
    userTokens = userTokens.filter((t) => t.count > 0)
    userTotalCents = userTokens.reduce(
      (sum, t) => sum + t.valueCents * t.count,
      0,
    )
  } catch {
    throw new Error(
      'Legacy money payload detected: expected canonical JSON token-set payload (e.g. [{"valueCents":50,"count":2}])',
    )
  }

  if (!question.data?.money) {
    logError('validateMoney: Missing money config in question.data.money')
    return {
      isCorrect: false,
      distractorIndex: 0,
      displayAnswer: formatMoneyDisplay(userTotalCents),
      errorType: 'MISSING_CONFIG',
    }
  }
  const moneyConfig = question.data.money
  const targetCents = moneyConfig?.targetCents || 0
  const allowedDenoms = moneyConfig?.allowedDenoms || []
  const constraints = moneyConfig.constraints || {}

  const violations: string[] = []

  const allowedValues = new Set(
    allowedDenoms.map((d: MoneyDenom) => d.valueCents),
  )
  for (const token of userTokens) {
    if (!allowedValues.has(token.valueCents)) {
      violations.push(`INVALID_DENOM_${token.valueCents}`)
    }
  }

  for (const token of userTokens) {
    if (!Number.isInteger(token.count) || token.count < 0) {
      violations.push('INVALID_TOKEN_COUNT')
    }
  }

  const totalTokenCount = userTokens.reduce((sum, t) => sum + t.count, 0)
  if (constraints.minTokens && totalTokenCount < constraints.minTokens) {
    violations.push(`TOO_FEW_TOKENS`)
  }
  if (constraints.maxTokens && totalTokenCount > constraints.maxTokens) {
    violations.push(`TOO_MANY_TOKENS`)
  }
  if (constraints.exactTokens && totalTokenCount !== constraints.exactTokens) {
    violations.push(`WRONG_TOKEN_COUNT`)
  }

  const hasBill = userTokens.some((t) => {
    const denom = allowedDenoms.find(
      (d: MoneyDenom) => d.valueCents === t.valueCents,
    )
    return denom?.type === 'bill' && t.count > 0
  })
  if (constraints.requireBill && !hasBill) {
    violations.push('MISSING_REQUIRED_BILL')
  }
  if (constraints.forbidBill && hasBill) {
    violations.push('FORBIDDEN_BILL_USED')
  }

  const amountCorrect = userTotalCents === targetCents
  const delta = Math.abs(userTotalCents - targetCents)

  const isCorrect = amountCorrect && violations.length === 0

  let errorType: string | undefined
  if (!isCorrect) {
    if (violations.length > 0) {
      errorType = violations[0]
    } else if (!amountCorrect) {
      if (userTotalCents < targetCents) {
        errorType = 'TOO_LOW'
      } else {
        errorType = 'TOO_HIGH'
      }

      if (delta === 5)
        errorType = userTotalCents < targetCents ? 'TOO_LOW_5C' : 'TOO_HIGH_5C'
      else if (delta === 10)
        errorType =
          userTotalCents < targetCents ? 'TOO_LOW_10C' : 'TOO_HIGH_10C'
      else if (delta === 20)
        errorType =
          userTotalCents < targetCents ? 'TOO_LOW_20C' : 'TOO_HIGH_20C'
      else if (delta === 50)
        errorType =
          userTotalCents < targetCents ? 'TOO_LOW_50C' : 'TOO_HIGH_50C'
      else if (delta === 100)
        errorType =
          userTotalCents < targetCents ? 'TOO_LOW_100C' : 'TOO_HIGH_100C'
    }
  }

  let distractorIndex = 0
  if (!isCorrect) {
    if (userTotalCents < targetCents) {
      if (delta === 50) distractorIndex = 3
      else if (delta === 100) distractorIndex = 4
      else distractorIndex = 1
    } else {
      distractorIndex = 2
    }
  }
  return {
    isCorrect,
    distractorIndex,
    displayAnswer: formatMoneyDisplay(userTotalCents),
    correctAnswerDisplay: formatMoneyDisplay(targetCents),
    errorType,
    delta,
  }
}

// ── Fill In ──────────────────────────────────────────────────

export const validateFillIn: ValidatorFn = (userAnswer, question) => {
  const config = question.data || {}
  const acceptedAnswers = config.acceptedAnswers || [
    String(question.correctAnswer),
  ]
  const caseSensitive = config.caseSensitive || false
  const userStr = String(userAnswer).trim()

  const isCorrect = acceptedAnswers.some((ans: string) => {
    const accepted = String(ans).trim()

    if (
      caseSensitive
        ? accepted === userStr
        : accepted.toLowerCase() === userStr.toLowerCase()
    ) {
      return true
    }

    const userNum = parseFloat(normalizeNumeric(userStr))
    const acceptedNum = parseFloat(normalizeNumeric(accepted))
    if (!isNaN(userNum) && !isNaN(acceptedNum)) {
      return Math.abs(userNum - acceptedNum) < NUMERIC_TOLERANCE
    }

    return false
  })

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: userStr,
  }
}

// ── Number Sequence ──────────────────────────────────────────

export const validateNumberSequence: ValidatorFn = (userAnswer, question) => {
  const userValues = String(userAnswer)
    .split(',')
    .map((s) => s.trim())
  const config = question.data || {}
  const rawCorrectValues: unknown[] = config.correctValues
    ? config.correctValues
    : config.correctValue !== undefined
      ? [config.correctValue]
      : []
  const correctValues = rawCorrectValues.map(String)

  const isCorrect =
    userValues.length === correctValues.length &&
    userValues.every((userVal: string, index: number) => {
      const correctVal = correctValues[index]

      if (userVal.toLowerCase() === correctVal.toLowerCase()) return true

      const userNum = parseFloat(userVal.replace(/[€\s,]/g, '.'))
      const correctNum = parseFloat(correctVal.replace(/[€\s,]/g, '.'))
      if (!isNaN(userNum) && !isNaN(correctNum)) {
        return Math.abs(userNum - correctNum) < NUMERIC_TOLERANCE
      }

      if (userVal.includes(':') && correctVal.includes(':')) {
        return userVal === correctVal
      }
      return false
    })
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: userValues.join(', '),
  }
}

// ── Ratio Table ──────────────────────────────────────────────

export const validateRatioTable: ValidatorFn = (userAnswer, question) => {
  let userInputs: Record<string, string> = {}
  try {
    userInputs = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }
  const config = question.data || {}
  const rows = config.rows || []
  const hiddenCells: Array<{ row: number; col: number }> =
    config.hiddenCells || []
  const isCorrect = hiddenCells.every((cell) => {
    const key = `${cell.row}-${cell.col}`
    const userVal = userInputs[key] || ''
    const correctVal = String(rows[cell.row]?.[cell.col] || '')
    if (userVal.trim() === correctVal.trim()) return true
    const userNum = parseFloat(normalizeNumeric(userVal))
    const correctNum = parseFloat(normalizeNumeric(correctVal))
    if (!isNaN(userNum) && !isNaN(correctNum)) {
      return Math.abs(userNum - correctNum) < NUMERIC_TOLERANCE
    }
    return false
  })
  const displayParts = hiddenCells.map((cell) => {
    const key = `${cell.row}-${cell.col}`
    return userInputs[key] || '?'
  })
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: displayParts.join(', '),
  }
}

// ── Estimation Slider ────────────────────────────────────────

export const validateEstimationSlider: ValidatorFn = (userAnswer, question) => {
  const userValue = Number(userAnswer)
  const config = question.data || {}
  const correctValue = config.correctValue || 0
  const tolerancePercent = config.tolerancePercent || 10
  const max = config.max || 100
  const min = config.min || 0

  const range = max - min
  const tolerance = range * (tolerancePercent / 100)
  const isCorrect = Math.abs(userValue - correctValue) <= tolerance

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: String(userValue),
    correctAnswerDisplay: String(correctValue),
  }
}

// ── Grid Shade ───────────────────────────────────────────────

export const validateGridShade: ValidatorFn = (userAnswer, question) => {
  let selectedIndices: number[] = []
  try {
    selectedIndices = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }

  const config =
    question.data?.gridShade || (question.data as Record<string, unknown>) || {}
  const targetCount = config.targetCount || 0

  const isCorrect = selectedIndices.length === targetCount

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${selectedIndices.length} vakjes gekleurd`,
  }
}

// ── Step Solver ──────────────────────────────────────────────

export const validateStepSolver: ValidatorFn = (userAnswer, question) => {
  let userSteps: Record<string, string | number> = {}
  try {
    userSteps = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }

  const config =
    question.data?.stepSolver ||
    (question.data as Record<string, unknown>) ||
    {}
  const steps = config.steps || []

  let correctCount = 0

  const isCorrect =
    steps.length > 0 &&
    steps.every(
      (
        step: {
          prompt?: string
          correctAnswer?: string | number
          acceptedAnswers?: (string | number)[]
          hint?: string
        },
        index: number,
      ) => {
        const userVal = String(userSteps[index] || '').trim()
        const accepted = step.acceptedAnswers || [step.correctAnswer]

        const match = accepted.some((ans: string | number) => {
          const acceptedStr = String(ans).trim()
          if (acceptedStr.toLowerCase() === userVal.toLowerCase()) return true

          const userNum = parseFloat(normalizeNumeric(userVal))
          const acceptedNum = parseFloat(normalizeNumeric(acceptedStr))
          if (!isNaN(userNum) && !isNaN(acceptedNum)) {
            return Math.abs(userNum - acceptedNum) < NUMERIC_TOLERANCE
          }
          return false
        })

        if (match) correctCount++
        return match
      },
    )

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${correctCount}/${steps.length} stappen correct`,
  }
}

// ── Multiplication Grid ──────────────────────────────────────

export const validateMultiplicationGrid: ValidatorFn = (
  userAnswer,
  question,
) => {
  let userInputs: Record<string, string> = {}
  try {
    userInputs = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }

  const config = question.data || {}
  const mode = config.mode || 'single_table'

  let allCorrect = true
  let totalCells = 0
  let correctCells = 0

  if (mode === 'single_table') {
    const baseNumber = config.baseNumber || 1
    const range: number[] = config.range || []
    const hiddenCells: number[] = config.hiddenCells || []
    totalCells = hiddenCells.length

    allCorrect = hiddenCells.every((cellIndex: number) => {
      const multiplier = range[cellIndex]
      const correctValue = baseNumber * multiplier
      const userVal = userInputs[String(cellIndex)] || ''
      const userNum = parseFloat(normalizeNumeric(userVal))
      const isMatch =
        !isNaN(userNum) && Math.abs(userNum - correctValue) < NUMERIC_TOLERANCE
      if (isMatch) correctCells++
      return isMatch
    })
  } else {
    // cross_table
    const rows: number[] = config.rows || []
    const cols: number[] = config.cols || []
    const hiddenCells: Array<{ row: number; col: number }> =
      config.hiddenCells || []
    totalCells = hiddenCells.length

    allCorrect = hiddenCells.every((cell: { row: number; col: number }) => {
      const correctValue = rows[cell.row] * cols[cell.col]
      const key = `${cell.row}-${cell.col}`
      const userVal = userInputs[key] || ''
      const userNum = parseFloat(normalizeNumeric(userVal))
      const isMatch =
        !isNaN(userNum) && Math.abs(userNum - correctValue) < NUMERIC_TOLERANCE
      if (isMatch) correctCells++
      return isMatch
    })
  }

  return {
    isCorrect: allCorrect,
    distractorIndex: allCorrect ? undefined : 0,
    displayAnswer: `${correctCells}/${totalCells} correct`,
  }
}

// ── Thermometer Slider ───────────────────────────────────────

export const validateThermometerSlider: ValidatorFn = (
  userAnswer,
  question,
) => {
  const userNum = Number(userAnswer)
  const correctNum = Number(question.correctAnswer)
  return {
    isCorrect:
      !isNaN(userNum) &&
      !isNaN(correctNum) &&
      Math.abs(userNum - correctNum) < NUMERIC_TOLERANCE,
  }
}

// ── Square Root Builder ──────────────────────────────────────

export const validateSquareRootBuilder: ValidatorFn = (
  userAnswer,
  question,
) => {
  const userSize = Number(userAnswer)
  const config = question.data?.squareRootBuilder || { targetValue: 16 }
  const targetValue = config.targetValue || 16

  const userArea = userSize * userSize
  const isCorrect = userArea === targetValue

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${userSize} × ${userSize} = ${userArea}`,
    correctAnswerDisplay: `√${targetValue} = ${Math.sqrt(targetValue)}`,
  }
}

// ── Scientific Notation Builder ──────────────────────────────

export const validateScientificNotationBuilder: ValidatorFn = (
  userAnswer,
  question,
) => {
  const config = question.data?.scientificNotationBuilder
  const target = Number(config?.targetValue)

  let coefficient: number | null = null
  let exponent: number | null = null
  try {
    const parsed = JSON.parse(String(userAnswer))
    if (parsed && typeof parsed === 'object') {
      const c = Number(parsed.coefficient)
      const e = Number(parsed.exponent)
      if (Number.isFinite(c)) coefficient = c
      if (Number.isFinite(e) && Number.isInteger(e)) exponent = e
    }
  } catch {
    /* malformed payload */
  }

  if (coefficient === null || exponent === null || !Number.isFinite(target)) {
    return {
      isCorrect: false,
      distractorIndex: 0,
      displayAnswer: String(userAnswer),
    }
  }

  const product = coefficient * Math.pow(10, exponent)
  // Relative tolerance — large target values (1e8) need bigger absolute slack.
  const tol = Math.max(NUMERIC_TOLERANCE, Math.abs(target) * 1e-9)
  const matchesTarget = Math.abs(product - target) <= tol
  // Canonical form: 1 ≤ a < 10 (strict on the upper bound).
  const isCanonical = coefficient >= 1 && coefficient < 10

  const isCorrect = matchesTarget && isCanonical

  const nlNumber = new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: 6,
  })
  const displayAnswer = `${nlNumber.format(coefficient)} × 10^${exponent}`

  let errorType: string | undefined
  if (!isCorrect) {
    if (matchesTarget && !isCanonical) {
      errorType = 'NON_CANONICAL'
    } else if (!matchesTarget) {
      errorType = 'WRONG_VALUE'
    }
  }

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer,
    errorType,
  }
}

// ── Place Value Build ────────────────────────────────────────

export const validatePlaceValueBuild: ValidatorFn = (userAnswer, question) => {
  const userNum = Number(userAnswer)
  const target = Number(question.correctAnswer)
  const isCorrect = !isNaN(userNum) && !isNaN(target) && userNum === target

  return {
    isCorrect,
    displayAnswer: String(userAnswer),
  }
}

// ── Bar Model Part ───────────────────────────────────────────

export const validateBarModelPart: ValidatorFn = (userAnswer, question) => {
  const normalized = String(userAnswer).replace(',', '.').trim()
  const userNum = parseFloat(normalized)
  const target = Number(question.correctAnswer)

  const isCorrect =
    !isNaN(userNum) &&
    !isNaN(target) &&
    Math.abs(userNum - target) < NUMERIC_TOLERANCE

  const config = question.data?.barModelPart
  const total = config?.total
  const segments = config?.segments
  const unit = config?.unit ?? ''

  let errorType: string | undefined
  if (!isCorrect && !isNaN(userNum) && !isNaN(target)) {
    if (
      typeof total === 'number' &&
      Math.abs(userNum - total) < NUMERIC_TOLERANCE
    ) {
      errorType = 'GAVE_WHOLE'
    } else if (
      typeof total === 'number' &&
      typeof segments === 'number' &&
      segments > 0 &&
      Math.abs(userNum - total / segments) < NUMERIC_TOLERANCE
    ) {
      errorType = 'GAVE_ONE_PART'
    } else if (userNum < target) {
      errorType = 'TOO_LOW'
    } else {
      errorType = 'TOO_HIGH'
    }
  }

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${unit}${userNum.toLocaleString('nl-NL')}`,
    correctAnswerDisplay: `${unit}${target.toLocaleString('nl-NL')}`,
    errorType,
  }
}

// ── Strategy Solver ──────────────────────────────────────────

export const validateStrategySolver: ValidatorFn = (userAnswer, question) => {
  let payload: { strategyId?: string; answer?: string } = {}
  try {
    payload = JSON.parse(String(userAnswer))
  } catch {
    // Fall back to treating the raw value as the answer
    payload = { answer: String(userAnswer) }
  }

  const config =
    (question.data?.strategySolver as
      | {
          acceptedAnswers?: (string | number)[]
          strategies?: { id: string; tier: string }[]
        }
      | undefined) || {}
  const accepted: (string | number)[] =
    config.acceptedAnswers ??
    (question.correctAnswer !== undefined ? [question.correctAnswer] : [])

  const userVal = String(payload.answer ?? '').trim()
  const isAnswerCorrect = accepted.some((ans) => {
    const acceptedStr = String(ans).trim()
    if (acceptedStr.toLowerCase() === userVal.toLowerCase()) return true
    const userNum = parseFloat(normalizeNumeric(userVal))
    const acceptedNum = parseFloat(normalizeNumeric(acceptedStr))
    if (!isNaN(userNum) && !isNaN(acceptedNum)) {
      return Math.abs(userNum - acceptedNum) < NUMERIC_TOLERANCE
    }
    return false
  })

  const strategies = config.strategies || []
  const chosenStrategy = strategies.find((s) => s.id === payload.strategyId)
  const isOptimal = chosenStrategy?.tier === 'optimal'

  // Scoring is binary on the ANSWER. The chosen strategy is consumed as
  // didactic feedback only — it must NOT gate correctness. Previously
  // `isAnswerCorrect && isOptimal` caused correct answers via a valid but
  // non-optimal strategy to score 0 (the "right answer, still 0/6" bug).
  const isCorrect = isAnswerCorrect

  // Surface the strategy quality for feedback without affecting the score.
  let errorType: string | undefined
  if (!isCorrect) {
    errorType = 'WRONG_VALUE'
  } else if (chosenStrategy && !isOptimal) {
    errorType = 'SUBOPTIMAL_STRATEGY'
  }

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: payload.strategyId
      ? `${userVal} (via ${payload.strategyId})`
      : userVal,
    errorType,
  }
}

// ── Fraction Strip Builder ───────────────────────────────────

export const validateFractionStripBuilder: ValidatorFn = (
  userAnswer,
  question,
) => {
  let payload: { divisions?: number; shaded?: number[] } = {}
  try {
    payload = JSON.parse(String(userAnswer))
  } catch {
    /* empty */
  }

  const config = question.data?.fractionStripBuilder || question.data || {}
  const targetDenominator = config.denominator || 1
  const targetNumerator = config.targetNumerator || 0
  const mode = config.mode || 'both'

  const divisions = payload.divisions || 1
  const shadedCount = payload.shaded?.length || 0

  const isCorrect =
    divisions === targetDenominator &&
    (mode === 'divide' || shadedCount === targetNumerator)

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${shadedCount}/${divisions}`,
    correctAnswerDisplay:
      mode === 'divide'
        ? `Verdeeld in ${targetDenominator}`
        : `${targetNumerator}/${targetDenominator}`,
  }
}

// ── Double Number Line ──────────────────────────────────────

export const validateDoubleNumberLine: ValidatorFn = (userAnswer, question) => {
  const userNum = Number(userAnswer)
  const config = question.data?.doubleNumberLine
  if (!config) {
    return {
      isCorrect: false,
      distractorIndex: 0,
      displayAnswer: String(userAnswer),
      errorType: 'MISSING_CONFIG',
    }
  }

  const correctValue = config.correctValue
  const askedScale =
    config.askFor === 'top' ? config.topScale : config.bottomScale
  const range = Math.abs((askedScale?.max ?? 100) - (askedScale?.min ?? 0))
  const tolerancePercent = config.tolerancePercent ?? 5
  const tolerance = (range * tolerancePercent) / 100

  const isCorrect =
    !isNaN(userNum) &&
    !isNaN(correctValue) &&
    Math.abs(userNum - correctValue) <= tolerance

  const unit = askedScale?.unit ?? ''
  const formatVal = (v: number): string => {
    const abs = Math.abs(v)
    const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : 2
    return `${Number(v.toFixed(decimals)).toLocaleString('nl-NL')}${unit}`
  }

  let errorType: string | undefined
  if (!isCorrect && !isNaN(userNum)) {
    const delta = userNum - correctValue
    if (Math.abs(delta) <= tolerance * 2) {
      errorType = delta < 0 ? 'CLOSE_LOW' : 'CLOSE_HIGH'
    } else {
      errorType = delta < 0 ? 'TOO_LOW' : 'TOO_HIGH'
    }
  }

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: formatVal(userNum),
    correctAnswerDisplay: formatVal(correctValue),
    errorType,
  }
}

// ── Comma Shift ─────────────────────────────────────────────

/**
 * Normalise a Dutch decimal string for equality comparison.
 * - "3,45"   -> "3.45"
 * - "3,450"  -> "3.45"
 * - "03,5"   -> "3.5"
 * - "0,50"   -> "0.5"
 * - ",5"     -> "0.5"
 * - "12,"    -> "12"
 * - "12"     -> "12"
 */
function normaliseCommaShiftAnswer(value: string): string {
  let v = String(value).trim().replace(',', '.')
  if (v.startsWith('.')) v = `0${v}`
  if (v.endsWith('.')) v = v.slice(0, -1)
  if (v.includes('.')) {
    let [intPart, fracPart] = v.split('.')
    intPart = intPart.replace(/^0+/, '') || '0'
    fracPart = fracPart.replace(/0+$/, '')
    v = fracPart.length > 0 ? `${intPart}.${fracPart}` : intPart
  } else {
    v = v.replace(/^0+/, '') || '0'
  }
  return v
}

export const validateCommaShift: ValidatorFn = (userAnswer, question) => {
  const config = question.data?.commaShift
  if (!config) {
    return {
      isCorrect: false,
      distractorIndex: 0,
      displayAnswer: String(userAnswer),
      errorType: 'MISSING_CONFIG',
    }
  }

  const userNorm = normaliseCommaShiftAnswer(String(userAnswer))
  const correctNorm = normaliseCommaShiftAnswer(config.correctAnswer)

  const accepted = config.acceptedAnswers ?? []
  const acceptedNorms = accepted.map(normaliseCommaShiftAnswer)

  const isCorrect = userNorm === correctNorm || acceptedNorms.includes(userNorm)

  // Heuristic error-typing for didactic feedback.
  let errorType: string | undefined
  if (!isCorrect) {
    const userNum = parseFloat(userNorm)
    const correctNum = parseFloat(correctNorm)
    if (!isNaN(userNum) && !isNaN(correctNum) && correctNum !== 0) {
      const ratio = userNum / correctNum
      // Off-by-one komma-plaats — extremely common: ×10 instead of ×100 or vice versa
      if (Math.abs(ratio - 10) < 1e-6 || Math.abs(ratio - 0.1) < 1e-6) {
        errorType = 'COMMA_OFF_BY_ONE'
      } else if (
        Math.abs(ratio - 100) < 1e-6 ||
        Math.abs(ratio - 0.01) < 1e-6
      ) {
        errorType = 'COMMA_OFF_BY_TWO'
      } else if (userNorm === normaliseCommaShiftAnswer(config.startValue)) {
        // Student didn't move the comma at all
        errorType = 'NO_SHIFT'
      } else {
        errorType = 'WRONG_VALUE'
      }
    } else {
      errorType = 'WRONG_VALUE'
    }
  }

  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: String(userAnswer),
    correctAnswerDisplay: config.correctAnswer,
    errorType,
  }
}
